const LOG_LEVELS = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

const getProcessEnv = (key) =>
  typeof process !== "undefined" ? process.env[key] : undefined;

const service = getProcessEnv("DD_SERVICE") || "pixelmixer-webapp";
const env = getProcessEnv("DD_ENV") || getProcessEnv("NODE_ENV") || "development";
const version = getProcessEnv("DD_VERSION") || getProcessEnv("APP_VERSION");
const minLevel = LOG_LEVELS[getProcessEnv("LOG_LEVEL")]
  ? getProcessEnv("LOG_LEVEL")
  : "info";
const sensitiveKeyPattern =
  /authorization|cookie|password|secret|token|api[_-]?key|session|x-session-key/i;

const isPlainObject = (value) =>
  value !== null &&
  typeof value === "object" &&
  Object.getPrototypeOf(value) === Object.prototype;

const serializeError = (error) => {
  if (!error) {
    return undefined;
  }

  if (error instanceof Error) {
    return {
      kind: error.name,
      message: error.message,
      stack: error.stack,
      status: error.status,
      data: sanitize(error.data),
    };
  }

  if (typeof error === "object") {
    return sanitize(error);
  }

  return {
    message: String(error),
  };
};

const sanitize = (value, depth = 0) => {
  if (depth > 5) {
    return "[MaxDepth]";
  }

  if (value instanceof Error) {
    return serializeError(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitize(item, depth + 1));
  }

  if (value instanceof URL) {
    return value.toString();
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entryValue]) => [
        key,
        sensitiveKeyPattern.test(key)
          ? "[REDACTED]"
          : sanitize(entryValue, depth + 1),
      ])
    );
  }

  return value;
};

const writeLog = (level, message, context = {}, error) => {
  if (LOG_LEVELS[level] < LOG_LEVELS[minLevel]) {
    return;
  }

  const normalizedContext =
    context instanceof Error
      ? { error: serializeError(context) }
      : sanitize(context);
  const normalizedError = error
    ? serializeError(error)
    : normalizedContext?.error;
  const entry = {
    timestamp: new Date().toISOString(),
    status: level,
    level,
    service,
    env,
    ...(version ? { version } : {}),
    message,
    ...(isPlainObject(normalizedContext)
      ? normalizedContext
      : { context: normalizedContext }),
    ...(normalizedError ? { error: normalizedError } : {}),
  };
  const line = `${JSON.stringify(entry)}\n`;

  if (typeof process === "undefined") {
    return;
  }

  if (level === "error" || level === "warn") {
    process.stderr.write(line);
    return;
  }

  process.stdout.write(line);
};

export const serverLogger = {
  debug: (message, context = {}) => writeLog("debug", message, context),
  info: (message, context = {}) => writeLog("info", message, context),
  warn: (message, context = {}, error) =>
    writeLog("warn", message, context, error),
  error: (message, context = {}, error) =>
    writeLog("error", message, context, error),
};
